import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Robot, Lightning, ChartLineUp, Warning, CheckCircle, ArrowsClockwise,
  PaperPlaneRight, X, ArrowRight, Sparkle, Pulse, Brain, Target,
  ThumbsDown, Plus, Upload, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

export default function AgentPage() {
  // Tâche 1368 (Eric 2026-06-30) — Page entièrement française.
  const { lang, tr } = useLang();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [insights, setInsights] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, i, g] = await Promise.all([
        axios.get(`${API}/api/agent/status`, { withCredentials: true }),
        axios.get(`${API}/api/agent/insights?status=active&limit=50`, { withCredentials: true }),
        axios.get(`${API}/api/agent/feedback-gaps?limit=5&days=30`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setInsights(i.data.insights || []);
      setGaps(g.data.gaps || []);
    } catch (e) {
      toast.error(tr("Erreur de chargement de l'agent", "Failed to load the agent"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const runCycle = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/api/agent/run-cycle`, {}, { withCredentials: true });
      toast.success(tr(
        `Analyse terminée : score ${r.data.score}/100, ${r.data.insights_created} nouveaux constats`,
        `Analysis complete: score ${r.data.score}/100, ${r.data.insights_created} new findings`,
      ));
      loadAll();
    } catch (e) {
      // Vincent XLSX 2026-07-09 #6 — message d'erreur clair et complet
      const detail = e?.response?.data?.detail || e?.message || tr("erreur inconnue", "unknown error");
      toast.error(`${tr("Erreur du cycle d'analyse : ", "Analysis cycle error: ")}${detail}`, { duration: 8000 });
    } finally {
      setRunning(false);
    }
  };

  const dismissInsight = async (id) => {
    try {
      await axios.post(`${API}/api/agent/insights/${id}/dismiss`, {}, { withCredentials: true });
      setInsights(insights.filter((i) => i.id !== id));
    } catch (e) {
      toast.error(tr("Erreur", "Error"));
    }
  };

  const [generatingIdx, setGeneratingIdx] = useState(null);
  const [draft, setDraft] = useState(null);

  const generateQA = async (gap, idx) => {
    setGeneratingIdx(idx);
    setDraft(null);
    try {
      const r = await axios.post(
        `${API}/api/agent/generate-qa`,
        { question: gap.question },
        { withCredentials: true }
      );
      if (r.data.status === "insufficient_context") {
        toast.warning(tr("Contexte insuffisant", "Not enough context"), { description: r.data.suggestion });
        setDraft({ ...r.data, gap_idx: idx });
      } else {
        toast.success(tr("Brouillon Q&R généré ! En attente de révision.", "Q&A draft generated. Awaiting review."));
        setDraft({ ...r.data, gap_idx: idx });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de génération", "Generation failed"));
    } finally {
      setGeneratingIdx(null);
    }
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages([...messages, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/api/agent/chat`,
        { message: msg, session_id: sessionId },
        { withCredentials: true }
      );
      setSessionId(r.data.session_id);
      setMessages((prev) => [...prev, { role: "agent", content: r.data.response }]);
    } catch (e) {
      toast.error(tr("Erreur de communication", "Communication failed"));
    } finally {
      setSending(false);
    }
  };

  const score = status?.score ?? 0;
  const scoreColor = score >= 80 ? "text-teal-500" : score >= 50 ? "text-amber-500" : "text-red-500";
  const scoreBg = score >= 80 ? "from-teal-500/10 to-teal-500/5" : score >= 50 ? "from-amber-500/10 to-amber-500/5" : "from-red-500/10 to-red-500/5";

  const m = status?.metrics || {};

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6 animate-fade-in stagger-reveal">
      {/* Hero */}
      <div className={`relative overflow-hidden bg-gradient-to-br ${scoreBg} border border-border rounded-2xl p-6`}>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#001a5e] flex items-center justify-center">
              <Robot size={32} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-3xl font-bold" data-testid="agent-title">Agent Knoria</h1>
                <Sparkle size={18} weight="fill" className="text-amber-400" />
              </div>
              <p className="text-sm text-muted-foreground">
                {tr("Votre assistant IA senior — analyse, propose, agit pour cette organisation", "Your senior AI assistant — analyses, proposes and acts for this organisation")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className={`text-4xl font-bold ${scoreColor}`} data-testid="agent-score">{score}<span className="text-lg text-muted-foreground">/100</span></div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{tr("Score de santé", "Health score")}</div>
            </div>
            <Button
              onClick={runCycle}
              disabled={running}
              className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
              data-testid="btn-run-cycle"
            >
              <ArrowsClockwise size={16} className={`mr-2 ${running ? "animate-spin" : ""}`} />
              {running ? tr("Analyse…", "Analysing…") : tr("Lancer l'analyse", "Run the analysis")}
            </Button>
            <Button
              onClick={async () => {
                try {
                  const r = await axios.post(`${API}/api/agent/weekly-digest/send`, {}, { withCredentials: true });
                  if (r.data.email_status?.status === "sent") {
                    toast.success(tr("Score Knoria envoyé par email !", "Knoria score sent by email."));
                  } else if (r.data.email_status?.status === "skipped") {
                    toast.info(tr("Email désactivé (pas de clé Resend) — aperçu disponible à /agent/weekly-digest", "Email disabled (no Resend key) — preview available at /agent/weekly-digest"));
                  } else {
                    toast.warning(`${tr("Statut", "Status")} : ${r.data.email_status?.status || tr("inconnu", "unknown")}`);
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
                }
              }}
              variant="outline"
              className="border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
              data-testid="btn-send-digest"
            >
              {tr("📊 Envoyer le bilan", "📊 Send the report")}
            </Button>
            <Button
              onClick={() => {
                window.open(`${API}/api/agent/qa-export.csv?status=approved`, "_blank");
                toast.success(tr("Téléchargement CSV (format Vincent)…", "Downloading CSV (Vincent format)…"));
              }}
              variant="outline"
              className="border-teal-500/30 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950/30"
              data-testid="btn-export-csv"
            >
              {tr("📥 Export CSV (format Vincent)", "📥 CSV export (Vincent format)")}
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={ChartLineUp} label={tr("Vidéos", "Videos")} value={m.videos?.total ?? 0} sub={tr(`${m.videos?.completed ?? 0} complètes`, `${m.videos?.completed ?? 0} complete`)} color="blue" testid="metric-videos" />
        <MetricCard icon={Brain} label="Q&R" value={m.knowledge_units?.total ?? 0} sub={tr(`${m.knowledge_units?.pending_review ?? 0} en attente`, `${m.knowledge_units?.pending_review ?? 0} pending`)} color="violet" testid="metric-ku" />
        <MetricCard icon={Target} label={tr("Locuteurs", "Speakers")} value={m.speakers?.total ?? 0} sub={tr(`${m.speakers?.with_photo ?? 0} avec photo`, `${m.speakers?.with_photo ?? 0} with a photo`)} color="emerald" testid="metric-speakers" />
        <MetricCard
          icon={Pulse}
          label="Satisfaction"
          value={m.feedback?.satisfaction != null ? `${m.feedback.satisfaction}%` : "—"}
          sub={`${m.feedback?.up ?? 0} 👍 / ${m.feedback?.down ?? 0} 👎`}
          color={m.feedback?.satisfaction == null ? "amber" : m.feedback.satisfaction >= 80 ? "emerald" : m.feedback.satisfaction >= 50 ? "amber" : "red"}
          testid="metric-satisfaction"
        />
      </div>

      {/* Knowledge Gaps Widget */}
      {gaps.length > 0 && (
        <div className="bg-card border border-red-500/30 rounded-2xl p-6" data-testid="gaps-widget">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <ThumbsDown size={22} weight="duotone" className="text-red-500" />
                {tr(`Questions mal notées (${gaps.length})`, `Poorly rated questions (${gaps.length})`)}
                <span className="text-xs px-2 py-0.5 bg-red-500/10 text-red-500 rounded-full font-semibold uppercase tracking-wider">{tr("30 derniers jours", "last 30 days")}</span>
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {tr("Ces questions ont reçu un 👎 — l'Agent suggère de combler la lacune avec une nouvelle Q&R ou une vidéo.", "These questions got a 👎 — the Agent suggests filling the gap with a new Q&A or a video.")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/dashboard/agent/drafts")}
              variant="outline"
              size="sm"
              className="gap-2 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
              data-testid="btn-go-draft-queue"
            >
              <MagicWand size={14} weight="fill" />
              {tr("File de validation rapide", "Quick validation queue")}
              <ArrowRight size={12} />
            </Button>
          </div>
          <div className="space-y-3">
            {gaps.map((g, idx) => (
              <div key={idx} className="border border-border rounded-xl p-4 hover:border-red-500/40 transition-colors" data-testid={`gap-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <ThumbsDown size={16} weight="fill" className="text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight" data-testid={`gap-question-${idx}`}>
                      {g.question}
                    </p>
                    {g.answer_preview && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2 italic">
                        {tr("Réponse donnée", "Answer given")} : « {g.answer_preview} »
                      </p>
                    )}
                    {g.comments?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {g.comments.slice(0, 2).map((c, ci) => (
                          <p key={ci} className="text-xs px-2 py-1 bg-secondary/60 rounded text-muted-foreground">
                            💬 {c}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-500">
                        {g.down_count} 👎
                      </span>
                      <Button
                        size="sm"
                        onClick={() => generateQA(g, idx)}
                        disabled={generatingIdx !== null}
                        className="gap-1.5 h-7 text-xs bg-gradient-to-r from-[#002FA7] to-[#001a5e] hover:opacity-90 text-white"
                        data-testid={`btn-gap-generate-qa-${idx}`}
                      >
                        {generatingIdx === idx ? (
                          <><ArrowsClockwise size={12} className="animate-spin" /> {tr("Génération…", "Generating…")}</>
                        ) : (
                          <><MagicWand size={12} weight="fill" /> {tr("Générer Q&R auto", "Generate Q&A automatically")}</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/dashboard/knowledge/import")}
                        className="gap-1.5 h-7 text-xs"
                        data-testid={`btn-gap-create-qa-${idx}`}
                      >
                        <Plus size={12} /> {tr("Créer manuellement", "Create manually")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/dashboard/upload")}
                        className="gap-1.5 h-7 text-xs"
                        data-testid={`btn-gap-upload-${idx}`}
                      >
                        <Upload size={12} /> {tr("Téléverser une vidéo", "Upload a video")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setChatOpen(true);
                          setInput(`Comment combler cette lacune : "${g.question}" ?`);
                        }}
                        className="gap-1.5 h-7 text-xs text-[#002FA7]"
                        data-testid={`btn-gap-ask-agent-${idx}`}
                      >
                        <Robot size={12} /> {tr("Demander à l'Agent", "Ask the Agent")}
                      </Button>
                    </div>
                    {draft && draft.gap_idx === idx && (
                      <div className="mt-3 border border-[#002FA7]/30 bg-[#002FA7]/5 rounded-lg p-3" data-testid={`gap-draft-${idx}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Sparkle size={14} weight="fill" className="text-[#002FA7]" />
                            <span className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">
                              {tr("Brouillon de l'Agent", "Agent draft")} {draft.confidence != null && `· ${tr("Confiance", "Confidence")} ${Math.round(draft.confidence * 100)}%`}
                            </span>
                          </div>
                          {draft.status === "draft_created" && (
                            <Button
                              size="sm"
                              onClick={() => navigate("/dashboard/admin/review")}
                              className="gap-1.5 h-7 text-xs bg-teal-600 hover:bg-teal-700 text-white"
                              data-testid={`btn-gap-review-${idx}`}
                            >
                              <CheckCircle size={12} weight="fill" /> {tr("Valider en 1 clic", "Validate in one click")}
                            </Button>
                          )}
                        </div>
                        {draft.status === "insufficient_context" ? (
                          <p className="text-xs text-amber-700 dark:text-amber-300">
                            ⚠️ {tr("Contexte insuffisant", "Not enough context")} : {draft.suggestion}
                          </p>
                        ) : (
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold">{draft.title}</p>
                            <p className="text-xs text-muted-foreground italic">{tr("Q", "Q")} : {draft.question}</p>
                            <p className="text-xs">{draft.answer}</p>
                            {draft.primary_video_title && (
                              <p className="text-[10px] text-muted-foreground mt-1">
                                {tr("Source", "Source")} : {draft.primary_video_title}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Lightning size={22} weight="duotone" className="text-amber-500" />
              {tr(`Constats actifs (${insights.length})`, `Active findings (${insights.length})`)}
            </h2>
            <p className="text-xs text-muted-foreground">{tr("Constats et suggestions de l'agent — priorisés", "The agent's findings and suggestions — by priority")}</p>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">{tr("Chargement…", "Loading…")}</div>
        ) : insights.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle size={48} weight="duotone" className="text-teal-500 mx-auto mb-3" />
            <p className="font-semibold">{tr("Aucun constat actif", "No active finding")}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {tr("Lancez une nouvelle analyse pour découvrir des occasions d'amélioration.", "Run a new analysis to uncover opportunities.")}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="insights-list">
            {insights.map((i) => (
              <InsightCard key={i.id} insight={i} onDismiss={() => dismissInsight(i.id)} navigate={navigate} lang={lang} tr={tr} />
            ))}
          </div>
        )}
      </div>

      {/* Floating chat button */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-8 right-8 w-16 h-16 rounded-full bg-gradient-to-br from-[#002FA7] to-[#001a5e] hover:scale-110 transition-transform shadow-2xl shadow-[#002FA7]/40 flex items-center justify-center group"
          data-testid="btn-open-agent-chat"
        >
          <Robot size={28} weight="duotone" className="text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-400 rounded-full border-2 border-[#0a0a0f] animate-pulse" />
          <span className="absolute right-full mr-3 px-3 py-1.5 rounded-lg bg-card border border-border text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            {tr("Parler à l'Agent Knoria", "Talk to the Knoria Agent")}
          </span>
        </button>
      )}

      {/* Chat panel */}
      {chatOpen && (
        <div className="fixed bottom-8 right-8 w-[420px] max-w-[95vw] h-[600px] max-h-[85vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col z-50" data-testid="agent-chat-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-gradient-to-r from-[#002FA7] to-[#001a5e] rounded-t-2xl">
            <div className="flex items-center gap-2 text-white">
              <Robot size={20} weight="duotone" />
              <div>
                <div className="font-bold text-sm">Agent Knoria</div>
                <div className="text-[10px] opacity-80">{tr("En ligne", "Online")} · Score {score}/100</div>
              </div>
            </div>
            <button onClick={() => setChatOpen(false)} className="text-white/80 hover:text-white" data-testid="btn-close-chat">
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Robot size={40} weight="duotone" className="text-[#002FA7] mx-auto mb-3" />
                <p className="text-sm font-semibold mb-1">{tr("Bonjour ! Comment puis-je vous aider ?", "Hello. How can I help?")}</p>
                <p className="text-xs text-muted-foreground">
                  {tr("Je connais l'état de votre organisation. Posez-moi n'importe quelle question.", "I know the state of your organisation. Ask me anything.")}
                </p>
                <div className="flex flex-col gap-2 mt-4">
                  {[
                    tr("Quel est l'état de mon organisation ?", "What is the state of my organisation?"),
                    tr("Que devrais-je faire en priorité ?", "What should I do first?"),
                    tr("Comment améliorer la qualité des Q&R ?", "How can I improve the quality of the Q&A?"),
                  ].map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); setTimeout(sendMessage, 50); }}
                      className="text-xs px-3 py-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors text-left"
                      data-testid={`suggested-${q.slice(0,15)}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-[#002FA7] text-white"
                    : "bg-secondary text-foreground"
                }`}>
                  {m.role === "agent" ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl px-4 py-2 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#002FA7] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-[#002FA7] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-[#002FA7] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="border-t border-border p-3">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                placeholder={tr("Posez votre question...", "Ask your question…")}
                disabled={sending}
                className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                data-testid="input-agent-message"
              />
              <Button onClick={sendMessage} disabled={sending || !input.trim()} size="icon" className="bg-[#002FA7] hover:bg-[#002FA7]/90" data-testid="btn-send-message">
                <PaperPlaneRight size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color, testid }) {
  const colorMap = {
    blue: "text-blue-500 bg-blue-500/10",
    violet: "text-violet-500 bg-violet-500/10",
    emerald: "text-teal-500 bg-teal-500/10",
    amber: "text-amber-500 bg-amber-500/10",
    red: "text-red-500 bg-red-500/10",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function InsightCard({ insight, onDismiss, navigate, lang, tr }) {
  const sevColor = {
    high: "border-red-500/40 bg-red-500/5",
    medium: "border-amber-500/40 bg-amber-500/5",
    low: "border-blue-500/40 bg-blue-500/5",
    info: "border-teal-500/40 bg-teal-500/5",
  }[insight.severity] || "border-border";

  const Icon = insight.kind === "suggestion" ? Sparkle : Warning;

  // Tâche 1413 (Eric 2026-09-30) — Les constats sont ÉCRITS EN BASE par le
  // cycle d'analyse : ils ne sont pas traduisibles après coup. Le serveur
  // enregistre donc les deux langues, et un constat d'avant cette tâche
  // (sans champ anglais) reste affiché en français plutôt que vide.
  const champ = (cle) => (lang === "en" ? insight[`${cle}_en`] : null) || insight[cle];
  const gravite = {
    high: tr("élevée", "high"),
    medium: tr("moyenne", "medium"),
    low: tr("faible", "low"),
    info: tr("information", "info"),
  }[insight.severity] || insight.severity;
  const genre = insight.kind === "suggestion" ? tr("suggestion", "suggestion") : tr("constat", "finding");

  return (
    <div className={`border rounded-xl p-4 ${sevColor}`} data-testid={`insight-${insight.id}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} weight="duotone" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              {gravite}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {genre}
            </span>
          </div>
          <h4 className="font-semibold text-sm">{champ("title")}</h4>
          <p className="text-xs text-muted-foreground mt-1">{champ("description")}</p>
          {insight.cta && insight.cta_url && (
            <Button
              onClick={() => navigate(insight.cta_url)}
              variant="outline"
              size="sm"
              className="mt-3 gap-2"
              data-testid={`btn-cta-${insight.id}`}
            >
              {champ("cta")}
              <ArrowRight size={14} />
            </Button>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground p-1"
          title={tr("Écarter", "Dismiss")}
          data-testid={`btn-dismiss-${insight.id}`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
